import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  Typography,
  Box,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Assignment,
  Book,
  Person
} from '@mui/icons-material';
import { notificationService } from '../services/notificationService';
import type { Notification } from '../types/notification';
import { logger } from '../utils/logger';


export const SimpleNotificationBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationService.getNotifications({ page: 1, pageSize: 50 });
      setNotifications(response.notifications);
      setUnreadCount(response.unreadCount);
    } catch (error) {
      logger.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadNotifications();
    }
  }, [open]);

  const handleRefresh = async () => {
    await loadNotifications();
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      await loadNotifications();
    } catch (error) {
      logger.error('Error marking all as read:', error);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getNotificationIcon = (type: string | null | undefined) => {
    switch (type) {
      case 'lesson_plan':
        return <Assignment color="primary" />;
      case 'book':
        return <Book color="secondary" />;
      case 'user':
        return <Person color="success" />;
      default:
        return <NotificationsIcon />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return notificationService.formatTimestamp(timestamp);
  };

  return (
    <>
      <Tooltip title="Thông báo">
        <IconButton
          color="inherit"
          onClick={handleClick}
          aria-label="notifications"
        >
          <Badge badgeContent={unreadCount} color="error">
            {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 350,
            maxHeight: 400,
            mt: 1
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">Thông báo</Typography>
            <Box>
              <Button size="small" startIcon={<MarkEmailReadIcon />} onClick={handleMarkAllAsRead}>
                Đánh dấu đã đọc
              </Button>
              <Button size="small" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={loading}>
                Làm mới
              </Button>
            </Box>
          </Box>
          
          <Divider sx={{ mb: 1 }} />
        </Box>

        <List sx={{ maxHeight: 250, overflow: 'auto' }}>
          {loading ? (
            <ListItem>
              <Box display="flex" justifyContent="center" width="100%" py={2}>
                <CircularProgress size={24} />
              </Box>
            </ListItem>
          ) : notifications.length === 0 ? (
            <ListItem>
              <ListItemText 
                primary="Không có thông báo nào"
                secondary="Tất cả thông báo đã được đọc"
              />
            </ListItem>
          ) : (
            notifications.map((notification) => (
              <ListItem
                key={notification.id}
                sx={{
                  backgroundColor: notification.isRead ? 'transparent' : 'action.hover',
                  '&:hover': {
                    backgroundColor: 'action.selected'
                  }
                }}
              >
                <ListItemIcon>
                  {getNotificationIcon(notification.type)}
                </ListItemIcon>
                <ListItemText
                  primary={notification.title || 'Thông báo'}
                  secondary={`${notification.message}\n${formatTimestamp(notification.timestamp)}`}
                />
              </ListItem>
            ))
          )}
        </List>

        <Divider />
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Button size="small" fullWidth>
            Xem tất cả thông báo
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default SimpleNotificationBell;
